// Fuel purchases CSV import — pure helpers (spec 2026-10-13-fuel-phase2-3 §5.5).
import { wallClockToIso } from './purchasesModel';

export const HEADER_MAP = {
  date: 'purchasedAt', datetime: 'purchasedAt', time: 'purchasedTime',
  vehicle: 'vehicle', plate: 'vehicle', name: 'vehicle', device: 'vehicle',
  litres: 'litres', liters: 'litres', qty: 'litres', quantity: 'litres',
  price: 'pricePerLitre', unitprice: 'pricePerLitre',
  total: 'total', amount: 'total',
  station: 'station', location: 'station',
  odometer: 'odometerKm', km: 'odometerKm',
  ref: 'externalRef', reference: 'externalRef', receipt: 'externalRef', invoice: 'externalRef',
  currency: 'currency',
};

export function autoDetect(headers) {
  const mapping = {};
  headers.forEach((h, i) => {
    const key = String(h).trim().toLowerCase().replace(/[^a-z]/g, '');
    if (HEADER_MAP[key]) mapping[i] = HEADER_MAP[key];
  });
  return mapping;
}

/** RFC-4180-ish: quoted fields, "" escapes, commas/newlines inside quotes, CRLF, BOM. Blank lines are dropped. */
export function parseCsvText(text) {
  const src = String(text ?? '').replace(/^\uFEFF/, '');
  const records = [];
  let row = [];
  let field = '';
  let quoted = false;
  const endField = () => { row.push(field); field = ''; };
  const endRow = () => { endField(); if (row.some((c) => c.trim() !== '')) records.push(row); row = []; };
  for (let i = 0; i < src.length; i += 1) {
    const c = src[i];
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i += 1; } else quoted = false;
      } else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') endField();
    else if (c === '\r') { if (src[i + 1] === '\n') i += 1; endRow(); }
    else if (c === '\n') endRow();
    else field += c;
  }
  if (field !== '' || row.length) endRow();
  const [headers = [], ...rows] = records;
  return { headers: headers.map((h) => h.trim()), rows: rows.map((r) => r.map((c) => c.trim())) };
}

const norm = (v) => String(v ?? '').trim().toLowerCase();

/** exact name, then exact plate attribute, then a numeric device id. */
export function resolveVehicle(value, devices) {
  const v = norm(value);
  if (!v) return null;
  const list = Object.values(devices ?? {});
  const byName = list.find((d) => norm(d.name) === v);
  if (byName) return byName.id;
  const byPlate = list.find((d) => norm(d.attributes?.plate) === v || norm(d.attributes?.licensePlate) === v);
  if (byPlate) return byPlate.id;
  if (/^\d+$/.test(v)) {
    const byId = list.find((d) => Number(d.id) === Number(v));
    if (byId) return byId.id;
  }
  return null;
}

const pad = (n) => String(n).padStart(2, '0');
function parseWhen(value, tz) {
  const s = String(value ?? '').trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::\d{2})?)?$/);
  let parts = m && [m[1], m[2], m[3], m[4], m[5]];
  if (!parts) {
    m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::\d{2})?)?$/);
    parts = m && [m[3], m[2], m[1], m[4], m[5]];
  }
  if (parts) {
    const [y, mo, d, h = '0', mi = '0'] = parts;
    if (Number(mo) < 1 || Number(mo) > 12 || Number(d) < 1 || Number(d) > 31 || Number(h) > 23 || Number(mi) > 59) return null;
    const day = `${y}-${pad(mo)}-${pad(d)}`;
    if (Number.isNaN(Date.parse(`${day}T00:00:00Z`)) || new Date(`${day}T00:00:00Z`).getUTCDate() !== Number(d)) return null;
    return wallClockToIso(day, `${pad(h)}:${pad(mi)}`, tz);
  }
  if (/^\d{4}-\d{2}-\d{2}T[\d:.]+(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
    const ms = Date.parse(s);
    return Number.isNaN(ms) ? null : new Date(ms).toISOString();
  }
  return null;
}

// "120,5" is a decimal comma; "1,200" / "1,200,500" are thousands separators.
const positive = (v) => {
  let s = String(v ?? '').trim();
  if (s === '') return null;
  if (/^\d+,\d{1,2}$/.test(s)) s = s.replace(',', '.');
  else s = s.replace(/,/g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

export function toPurchaseRow(row, mapping, devices, tz) {
  const get = (field) => {
    const idx = Object.entries(mapping).find(([, f]) => f === field)?.[0];
    return idx === undefined ? '' : String(row[idx] ?? '').trim();
  };
  // A separate Time column joins a date-only value ("2026-10-12" + "08:30"); a date that already carries a time wins.
  const dateValue = get('purchasedAt'); const timeValue = get('purchasedTime');
  const purchasedAt = parseWhen(timeValue && /^[^\sT]+$/.test(dateValue) ? `${dateValue} ${timeValue}` : dateValue, tz);
  if (!purchasedAt) return { ok: false, error: 'date' };
  const deviceId = resolveVehicle(get('vehicle'), devices);
  if (deviceId === null) return { ok: false, error: 'vehicle' };
  const litres = positive(get('litres'));
  if (litres === null || litres <= 0) return { ok: false, error: 'litres' };
  const body = { purchasedAt, deviceId, litres };
  for (const f of ['pricePerLitre', 'total', 'odometerKm']) {
    const n = positive(get(f));
    if (n !== null) body[f] = n;
  }
  for (const f of ['station', 'externalRef', 'currency']) {
    const v = get(f);
    if (v) body[f] = v;
  }
  return { ok: true, body };
}
